import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkspaces } from "../../../redux/Slice/Workspace/workspaceSlice";
import { useWhatsAppEmbeddedSignup } from "../../../features/signup/useWhatsAppEmbeddedSignup";
import { getAccessToken } from "../../../utils/tokenManager";
import { getAuthToken } from "../../../utils/getAuthToken";
import axios from "axios";

const FB_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "";
const FB_CONFIG_ID = process.env.REACT_APP_FACEBOOK_CONFIG_ID || "";
const FB_GRAPH_V = process.env.REACT_APP_FACEBOOK_GRAPH_VERSION || "v25.0";
const SERVER = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const ConnectWhatsAppDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const workspaceId = useSelector(
    (s) => s.workspace?.currentWorkspace?._id || s.auth?.user?.workspaceId
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const embedded = useWhatsAppEmbeddedSignup({
    appId: FB_APP_ID,
    configId: FB_CONFIG_ID,
    graphVersion: FB_GRAPH_V,
    enabled: open,
  });

  const handleClose = () => {
    embedded.resetFlow();
    setSubmitError("");
    onClose();
  };

  const handleConnect = async () => {
    setSubmitError("");
    if (!embedded.authCode) {
      setSubmitError("Complete the Facebook signup flow first.");
      return;
    }
    setSubmitting(true);
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      if (!token) {
        setSubmitError("Not authenticated.");
        return;
      }
      await axios.post(
        `${SERVER}/api/workspaces/${workspaceId}/connect-whatsapp`,
        {
          code: embedded.authCode,
          wabaId: embedded.sessionInfo?.wabaId,
          phoneNumberId: embedded.sessionInfo?.phoneNumberId,
          businessId: embedded.sessionInfo?.businessId,
          isCoexistence: !!embedded.sessionInfo?.isCoexistence,
          appID: FB_APP_ID,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await dispatch(fetchWorkspaces());
      handleClose();
    } catch (err) {
      setSubmitError(
        err.response?.data?.message || err.message || "Connection failed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { bgcolor: "background.paper" } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Connect WhatsApp
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
          Step 2 — Connect WhatsApp Business
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Connect your WhatsApp Business account with Meta&apos;s secure signup.
          After you finish in the Facebook window, click &ldquo;Connect WhatsApp&rdquo; below.
        </Typography>

        {(!FB_APP_ID || !FB_CONFIG_ID) ? (
          <Typography color="error" sx={{ mb: 2, fontSize: 14 }}>
            Facebook App ID or Config ID is not configured. Set{" "}
            <code>REACT_APP_FACEBOOK_APP_ID</code> and{" "}
            <code>REACT_APP_FACEBOOK_CONFIG_ID</code> in your client .env and restart.
          </Typography>
        ) : embedded.sdkError ? (
          <Typography color="error" sx={{ mb: 2, fontSize: 14 }}>
            {embedded.sdkError}
          </Typography>
        ) : (
          <Button
            fullWidth
            variant="contained"
            disabled={!embedded.sdkReady}
            onClick={embedded.launchEmbeddedSignup}
            sx={{
              mb: 2,
              py: 1.5,
              backgroundColor: "#1877f2",
              color: "#fff",
              "&:hover": { backgroundColor: "#166fe5" },
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {!embedded.sdkReady ? "Loading…" : "Continue with Facebook"}
          </Button>
        )}

        {embedded.authCode && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "12px",
              backgroundColor: "action.hover",
            }}
          >
            <Typography variant="body2">
              Facebook connected successfully. Click &ldquo;Connect WhatsApp&rdquo; below to finish.
            </Typography>
          </Box>
        )}

        {(embedded.loginError || submitError) && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {submitError || embedded.loginError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!embedded.authCode || submitting}
          onClick={handleConnect}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#25D366",
            "&:hover": { backgroundColor: "#1da851" },
            "&.Mui-disabled": { backgroundColor: "action.disabledBackground" },
          }}
        >
          {submitting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Connecting <CircularProgress size={16} sx={{ color: "inherit" }} />
            </Box>
          ) : (
            "Connect WhatsApp"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectWhatsAppDialog;
